import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface TournamentHistoryItem {
  id: string;
  name: string;
  organizer: string;
  date: string;
  sport: string;
  category: string;
  subcategory: string;
  result: 'champion' | 'finalist' | 'sf' | 'qf' | 'participation';
  points: number;
  format: string;
  source: 'internal' | 'external';
}

const canonicalSports: Record<string, string> = {
  'padel': 'Padel',
  'tenis': 'Tenis',
  'pickleball': 'Pickleball',
};

const normalize = (s: string) =>
  s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

function deriveResult(breakdownJson: any, bracketStartRound: string | null, winsInBracket: number | null): TournamentHistoryItem['result'] {
  if (breakdownJson && typeof breakdownJson === 'object') {
    if (breakdownJson.champion) return 'champion';
    if (breakdownJson.subcampeon) return 'finalist';
    if (breakdownJson.sf) return 'sf';
    if (breakdownJson.qf || breakdownJson.octavos) return 'qf';
  }
  if (bracketStartRound && winsInBracket != null) {
    // Rounds needed to win the tournament from each starting round
    const roundsToWinMap: Record<string, number> = {
      'final': 1,
      'sf': 2,
      'qf': 3,
      'r16': 4, 'octavos': 4,
      'r32': 5,
    };
    const roundsToWin = roundsToWinMap[bracketStartRound.toLowerCase()] ?? 4;
    const matchesRemaining = roundsToWin - winsInBracket;
    if (matchesRemaining <= 0) return 'champion';
    if (matchesRemaining === 1) return 'finalist';
    if (matchesRemaining === 2) return 'sf';
    if (matchesRemaining === 3) return 'qf';
  }
  return 'participation';
}

export function useTournamentHistory(userId?: string) {
  const { user: authUser } = useAuth();
  const targetUserId = userId || authUser?.id;
  const [history, setHistory] = useState<TournamentHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!targetUserId) {
      setLoading(false);
      return;
    }

    const fetchHistory = async () => {
      setLoading(true);

      const { data: internalData } = await supabase
        .from('ranking_points_ledger')
        .select('id, tournament_id, sport, category, subcategory, points_awarded, breakdown_json, bracket_start_round, wins_in_bracket, created_at')
        .eq('user_id', targetUserId);

      const { data: externalData } = await supabase
        .from('external_tournament_results')
        .select('id, external_tournament_id, category, subcategory, result_stage, points_awarded, created_at')
        .eq('linked_user_id', targetUserId);

      const items: TournamentHistoryItem[] = [];

      // Collect all organizer user_ids to batch-fetch names
      const organizerUserIds = new Set<string>();

      // Process internal tournaments
      let eventsMap = new Map<string, any>();
      if (internalData?.length) {
        const tournamentIds = [...new Set(internalData.map(d => d.tournament_id))];
        const { data: events } = await supabase
          .from('events')
          .select('id, title, start_date, sport, tournament_format, organizer_id')
          .in('id', tournamentIds);
        eventsMap = new Map(events?.map(e => [e.id, e]) || []);
        events?.forEach(e => { if (e.organizer_id) organizerUserIds.add(e.organizer_id); });
      }

      let extMap = new Map<string, any>();
      if (externalData?.length) {
        const extIds = [...new Set(externalData.map(d => d.external_tournament_id))];
        const { data: extTournaments } = await supabase
          .from('external_tournaments')
          .select('id, name, tournament_date, sport, tournament_format, organizer_id')
          .in('id', extIds);
        extMap = new Map(extTournaments?.map(e => [e.id, e]) || []);
        extTournaments?.forEach(e => { if (e.organizer_id) organizerUserIds.add(e.organizer_id); });
      }

      // Fetch organizer names
      const organizerNames = new Map<string, string>();
      if (organizerUserIds.size > 0) {
        const { data: profiles } = await supabase
          .from('organizer_public_profiles')
          .select('user_id, company_name')
          .in('user_id', [...organizerUserIds]);
        profiles?.forEach(p => {
          if (p.user_id && p.company_name) organizerNames.set(p.user_id, p.company_name);
        });
      }

      if (internalData?.length) {
        for (const row of internalData) {
          const event = eventsMap.get(row.tournament_id);
          if (!event) continue; // Skip orphan records
          const sport = event?.sport || row.sport;
          const canonical = canonicalSports[normalize(sport)] || sport;
          items.push({
            id: row.id,
            name: event?.title || 'Torneo',
            organizer: (event?.organizer_id && organizerNames.get(event.organizer_id)) || '',
            date: event?.start_date || row.created_at,
            sport: canonical,
            category: row.category,
            subcategory: row.subcategory,
            result: deriveResult(row.breakdown_json, row.bracket_start_round, row.wins_in_bracket),
            points: row.points_awarded,
            format: event?.tournament_format || 'circuito',
            source: 'internal',
          });
        }
      }

      if (externalData?.length) {
        const resultMap: Record<string, TournamentHistoryItem['result']> = {
          'champion': 'champion', 'campeon': 'champion',
          'finalist': 'finalist', 'subcampeon': 'finalist',
          'sf': 'sf', 'qf': 'qf', 'participation': 'participation',
        };
        for (const row of externalData) {
          const ext = extMap.get(row.external_tournament_id);
          const sport = ext?.sport || '';
          const canonical = canonicalSports[normalize(sport)] || sport;
          items.push({
            id: row.id,
            name: ext?.name || 'Torneo Externo',
            organizer: (ext?.organizer_id && organizerNames.get(ext.organizer_id)) || '',
            date: ext?.tournament_date || row.created_at,
            sport: canonical,
            category: row.category,
            subcategory: row.subcategory,
            result: resultMap[row.result_stage?.toLowerCase()] || 'participation',
            points: row.points_awarded,
            format: ext?.tournament_format || 'circuito',
            source: 'external',
          });
        }
      }

      items.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      setHistory(items);
      setLoading(false);
    };

    fetchHistory();
  }, [targetUserId]);

  return { history, loading };
}
